/**
 * FLButton — Floodlight call to action (v1.71): a rounded pill on the
 * original glass look. On hover it lifts on a soft glow, the label rolls up
 * to a fresh copy and the arrow turns to point up-right; keyboard focus
 * lights it the same way and adds a purple ring.
 *
 *   <FLButton label="Run search" variant="green" icon="search" />  accent fill (purple → blue), the primary action
 *   <FLButton label="Start free" onPress={...} />                 strong glass (white text), a secondary primary
 *   <FLButton label="See plans" variant="outline" />               clear glass with a hairline
 *   <FLButton label="Compare" variant="paper" />                   the same, for glass stage panels
 */
import React from 'react';
import { View, Text, Pressable, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { IS_WEB, useReducedMotion } from '../../utils/motion';
import { ensureFloodlight } from './floodlightCss';
import { FL, FONT, ACCENT_GRADIENT } from './theme';

const SKIN = {
    ink: { bg: FL.stage, fg: FL.ink, border: FL.rule, blur: true },
    green: { bg: FL.green, fg: FL.onGreen, border: 'transparent', image: IS_WEB ? ACCENT_GRADIENT : undefined },
    outline: { bg: 'transparent', fg: FL.ink, border: FL.rule, blur: true },
    paper: { bg: FL.inkWash, fg: FL.onStage, border: FL.hair2, blur: true }, // on a glass stage
};
const BLUR = IS_WEB ? { backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' } : null;

export default function FLButton({
    label,
    onPress,
    variant = 'ink',
    icon,
    arrow = true,
    busy = false,
    busyLabel,
    disabled = false,
    size = 'md',
    style,
    accessibilityLabel,
    testID,
    ...rest
}) {
    if (IS_WEB) ensureFloodlight();
    const reduced = useReducedMotion();
    const skin = SKIN[variant] || SKIN.ink;
    const off = disabled || busy;
    const roll = IS_WEB && !reduced;
    const textStyle = [styles.text, size === 'lg' && styles.textLg, size === 'sm' && styles.textSm, { color: skin.fg }];
    const glyph = icon || (arrow ? 'arrow-forward' : null);

    return (
        <Pressable
            onPress={off ? undefined : onPress}
            disabled={off}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel || label}
            accessibilityState={{ disabled: !!off, busy: !!busy }}
            aria-disabled={off || undefined}
            aria-busy={busy || undefined}
            testID={testID}
            dataSet={{ flBtn: variant }}
            style={({ pressed }) => [
                styles.base,
                size === 'lg' && styles.lg,
                size === 'sm' && styles.sm,
                { backgroundColor: skin.bg, borderColor: skin.border },
                skin.image && { backgroundImage: skin.image },
                skin.blur && BLUR,
                off && !busy && styles.disabled,
                pressed && !off && styles.pressed,
                style,
            ]}
            {...rest}
        >
            {busy ? (
                <>
                    <ActivityIndicator size="small" color={skin.fg} />
                    {!!busyLabel && <Text style={textStyle} numberOfLines={1}>{busyLabel}</Text>}
                </>
            ) : (
                <>
                    {roll ? (
                        <View dataSet={{ flRoll: '' }}>
                            <Text style={textStyle} numberOfLines={1} dataSet={{ flLabel: 'a' }}>{label}</Text>
                            <Text style={textStyle} numberOfLines={1} dataSet={{ flLabel: 'b' }} aria-hidden>{label}</Text>
                        </View>
                    ) : (
                        <Text style={textStyle} numberOfLines={1}>{label}</Text>
                    )}
                    {!!glyph && (
                        <View dataSet={{ flIco: '' }} aria-hidden>
                            <Ionicons name={glyph} size={size === 'sm' ? 15 : 18} color={skin.fg} />
                        </View>
                    )}
                </>
            )}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    base: {
        minHeight: 48,
        paddingHorizontal: 24,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        borderWidth: 1,
        borderRadius: 999,
    },
    lg: { minHeight: 56, paddingHorizontal: 28 },
    sm: { minHeight: 44, paddingHorizontal: 16, gap: 10 },
    pressed: { opacity: 0.92 },
    disabled: { opacity: 0.45 },
    text: { fontFamily: FONT, fontSize: 13.5, fontWeight: '700', letterSpacing: 0.9, textTransform: 'uppercase' },
    textLg: { fontSize: 15 },
    textSm: { fontSize: 12.5, letterSpacing: 0.9 },
});
